import { crossingKey } from "./crossing";
import { Hotbar } from "./panels/shared";
import { formatWorldDate } from "../journal/journal";
import { MapPanel } from "../map/panel";
import type { PlayerPosition } from "../player/controller";
import type { WorldDateView } from "../world/types";
import {
  BookPanel,
  DialogueLine,
  ErasPanel,
  FeedbackLine,
  InteractPrompt,
  InventoryPanel,
  JournalOverlay,
  KeyCard,
  MarketPanel,
  MenuScreen,
  NamePrompt,
  OriginsPanel,
  SaveButton,
  TracePanel,
} from "./panels";

/*
 * The HUD: plain DOM, a handful of numbers, no framework.
 *
 * It shows what a prototype needs to be debuggable rather than what a game needs
 * to be playable: frame rate, draw calls, where the player is, how much world is
 * loaded and generated, and whether the host loop is keeping up. Three of them
 * earn their place by being the only visible sign of something:
 *
 * - **dropped ticks** — the fixed-timestep clamp engaged, which is the
 *   difference between "the sim is slow" and "the sim is behind and has given up
 *   on the backlog" (`host/timestep.ts`).
 * - **draw calls** — the number the rendering research says to measure rather
 *   than reason about, and the trigger for column aggregation if it climbs
 *   (`docs/research/rendering/threejs-chunks.md` §1).
 * - **the world hash** — the simulation is running, and this is what a replay
 *   of this session will be compared against.
 *
 * Phase 3 gave it five more surfaces — an interaction prompt, a spoken line, the
 * market board, the journal and the dev-only trace view — and they live in
 * `hud/panels.ts` rather than here. The split is by *kind* and not by size:
 * everything above is a number the client measured about itself and rewrites ten
 * times a second, and everything there is a thing the world said, written once
 * when it is said. Owned from here, so `main.ts` still talks to one object.
 *
 * The one Phase 3 number that belongs on *this* side is the date. It is not
 * something the world said, it is the clock the world said it by — and the
 * readout is where a tester looks to see that the simulation's calendar is
 * turning at all (`world/types.ts`, `WitnessSource.date`).
 */

/** How often the text is rewritten, in seconds. */
const REFRESH_SECONDS = 0.1;

/** Everything the HUD shows. Assembled by `main.ts` from the loop, the player and the world. */
export interface HudState {
  readonly fps: number;
  readonly position: PlayerPosition;
  readonly standing: boolean;
  readonly chunkCount: number;
  readonly columnCount: number;
  readonly pendingUploads: number;
  readonly tick: number;
  readonly clampedFrames: number;
  readonly droppedSteps: number;
  readonly worldHash: string;
  readonly dugTiles: number;
  readonly generatedChunks: number;
  readonly drawCalls: number;

  /**
   * The world's own date, or `null` in a session with no history to keep one.
   *
   * `null` is `?bootstrap=0` — the Phase 1 world, where the calendar exists but
   * nothing has ever happened in it and nobody is reading it (`world/wasm.ts`).
   * Shown rather than hidden the rest of the time because a date that moves is
   * the cheapest visible proof that the abstract tier is ticking under the
   * renderer.
   */
  readonly date: WorldDateView | null;
}

/**
 * The two surfaces a *session* has that a debug build does not: the keys a
 * first-time player is shown, and the button that hands them their replay.
 *
 * Optional, and the option is not laziness: `dev/selfcheck.ts` builds a `Hud`
 * to check the panels over and has neither a session to save nor a player to
 * greet, and a key card that existed in that context would be chrome nobody
 * asked for in a test nobody watches.
 */
export interface SessionChrome {
  /** The first-run card's lines: move, look, talk, journal. Nothing else. */
  readonly keys: readonly string[];

  readonly saveLabel: string;
  readonly saveHint: string;
  readonly onSave: () => void;
}

export class Hud {
  /** The "F — talk to Halvard Reedmere" line under the crosshair. */
  readonly prompt: InteractPrompt;

  /**
   * The "C — make camp until morning" line, top centre, shown while it is dark.
   *
   * Its own element rather than a second use of the aiming prompt, because the
   * two answer different questions and both can be true at once: one names what
   * the player is standing in front of and one names what the *night* has made
   * available (`main.ts`, Phase 5 decision 8). Sharing a slot would have the
   * night take the prompt off a person the player had just walked up to.
   */
  readonly camp: InteractPrompt;

  /**
   * The "J — Leifoddness has not heard 3 things" line, top centre under the
   * camp prompt.
   *
   * A third instance of the same element and a third question (Phase 5 decision
   * 4): what the *player* is carrying that the town they are standing in does
   * not have. Its own slot for the reason the camp prompt has one — all three
   * can be true at once, and a night, a person in front of you and word in your
   * pocket must not take each other's line away.
   */
  readonly word: InteractPrompt;

  /**
   * The "mouse — strike Halvard Reedmere" line, just above the aiming prompt.
   *
   * **What the left mouse button would do right now** (Phase 7, wave 2D-a): it
   * has meant *dig* since Phase 1 and now means *swing* whenever the strike pick
   * has somebody in it, so the one thing a player is owed is a sentence saying
   * which. Its own slot for the reason every prompt here has one — the aiming
   * prompt is about `F` and this is about the mouse, and both are true at once.
   *
   * The rule it is a rendering of lives in `witness/witness.ts`
   * (`Witnessing.swingPrompt`), because deciding that an armed hand pointed at a
   * person means a swing is a fact about the verb rather than about a `div`.
   */
  readonly swing: InteractPrompt;

  /**
   * The "R — loose at Halvard Reedmere · three arrows" line, above the swing's.
   *
   * **What the bow would do right now** (Phase 7, wave 2D-b): a fifth slot for
   * the reason the four above have their own — the mouse's line is about the
   * mouse and this is about `R`, and a player with a blade in one hand and a bow
   * over their shoulder is looking at both answers at once.
   *
   * The rule it renders lives in `witness/arms.ts` (`loosePrompt`), which is
   * also where the words are: what the bow may do is a fact about the verb
   * rather than about a `div`.
   */
  readonly loose: InteractPrompt;

  /**
   * How the player's own body is, bottom centre, and nothing when it is whole.
   *
   * **The simulation's own word and no number** (Phase 7's cross-cutting rule:
   * any damage number computed in the client is a bug by definition). What
   * arrives is one of four condition words and a mask of what has stopped
   * working; what this element does is draw the sentence
   * `witness/witness.ts` assembles out of them.
   *
   * A fifth `InteractPrompt` rather than a bar or a portrait, and deliberately
   * so: a readout that looked designed would be a design decision the
   * UI-direction conversation has not made yet, and what a playtest needs is to
   * be able to tell *hurt* from *about to die*.
   */
  readonly condition: InteractPrompt;

  /**
   * Why nothing happened, under the crosshair and then gone (`hud/panels.ts`).
   *
   * **The wave-1.5 graybox ruling**: every refused action displays a judgeable
   * message. Its own slot rather than a fourth use of the aiming prompt, for
   * the reason the camp and word prompts have theirs — both can be true at once,
   * and a refusal must not take the line off the thing the player is standing in
   * front of. It sits directly under the aiming prompt, which is where they are
   * already looking when the key they pressed is refused.
   */
  readonly feedback: FeedbackLine;

  /** The answer line, bottom centre: only ever what the player asked for. */
  readonly dialogue: DialogueLine;

  /**
   * The overheard line, as the subtitle band: the street speaking unprompted —
   * a staged reaction's voices (`witness/witness.ts`'s `stage`). Its own element
   * because the two are different kinds of hearing, and because sharing one slot
   * let a voice the player never addressed replace an answer they were
   * mid-reading.
   *
   * **It shares a column with the answer since chunk U-1** ({@link Hud.speech}),
   * and it is built *after* the answer on purpose — see that member.
   */
  readonly overheard: DialogueLine;

  /** The market board, filled when the player reads it. */
  readonly market: MarketPanel;

  /**
   * The four-page screen `Tab` opens: *you*, *journal*, *map*, *inventory*
   * (chunk U-3, `hud/panels/menu.ts`).
   *
   * **Built where the journal was built**, which in this HUD is what decides
   * what covers what: the constructor order is the document order and there is
   * no `z-index` anywhere in the client. The three surfaces it absorbed were
   * built at this point in the stack, so everything that used to be drawn over
   * them — the readout, the notice, the click-to-play overlay, the night's
   * blackout, and the eras and origins screens — is still drawn over this.
   */
  readonly menu: MenuScreen;

  /**
   * Everything the player has learned, and the page about the player: the
   * menu's second and first (`hud/panels/journal.ts`).
   *
   * `J` still lands on it and still closes it, which is the whole of what the
   * key ever did — what changed is that the panel is a page of a screen now, so
   * `open` means *the screen is up and showing the journal* and `hide` closes
   * the screen. `witness/witness.ts` asks and says exactly what it always did.
   */
  readonly journal: JournalOverlay;

  /** The same, grouped by record and spelled out from the log. Dev-only; T. */
  readonly trace: TracePanel;

  /**
   * What the player is carrying: the menu's fourth page, landed on with `I`
   * (`hud/panels/inventory.ts`).
   *
   * The journal's and the map's third sibling, and since chunk U-3 that is a
   * structural fact rather than a family resemblance: the three are pages of
   * one screen. The difference between them is which *kind* of holding — one is
   * what the player was told, one is where they have been, and this is what is
   * in their hands.
   */
  readonly inventory: InventoryPanel;

  /**
   * The ten slots the digit keys hold, drawn twice (chunk U-5).
   *
   * **One object and two views** — the strip in the corner of the screen, and
   * the one on the foot of the inventory page — so the two cannot disagree about
   * which key holds what. Owned here for the reason every panel is: `main.ts`
   * talks to one object, and the drain that turns a press into a
   * {@link WorldSource.wield} is beside the drains for every other key.
   *
   * What it holds is the **session's arrangement** and nothing the simulation
   * knows about (`docs/decisions/player.md` §24.1: which handle sits over which
   * key is the journal's kind of state). What lights a slot is the simulation's
   * own answer to *what is in your hand*, and never this client's memory of a
   * press.
   */
  readonly hotbar: Hotbar;

  /**
   * The map the player has drawn of the ground they have walked: the menu's
   * third page, landed on with `M`.
   *
   * The journal's sibling (Phase 5 decision 10) and a page of the same screen
   * since chunk U-3. Owned here so `main.ts` still talks to one object, and
   * drawn in `map/panel.ts` because it is a picture rather than a list.
   */
  readonly map: MapPanel;

  /**
   * A book opened, one line per record it cites (`hud/panels.ts`).
   *
   * The market board's sibling and not the journal's: it is walked up to,
   * opened with `F` and closed with `F`, and it does not join
   * {@link Hud.reading} — see {@link BookPanel} for why that is a decision.
   */
  readonly book: BookPanel;

  /**
   * The Book of Eras: the world's own history, one page per age
   * (`hud/panels.ts`, Phase 6 decision O6).
   *
   * One panel for both of its readings — the session opening on it, and the
   * copy standing on a town's shelf — because the exit criterion is that the
   * *same text* is readable in-world, and two panels would make that a claim
   * about two code paths instead of a fact about one.
   *
   * Not a reading panel, for {@link Hud.book}'s reason and with one addition:
   * the opening reading happens before the pointer lock exists at all, so there
   * is nothing to hand back and nothing to take.
   */
  readonly eras: ErasPanel;

  /**
   * The birth screen: the lives this world can offer, and the question that
   * leads to them (`hud/panels.ts`, Phase 6 decision 3).
   *
   * The Book of Eras' immediate sibling in every way that matters — it is up
   * before the pointer lock exists, it is read against the same darkened screen,
   * and it is skippable at every step — so it is not a reading panel either, for
   * {@link Hud.eras}'s reason: there is nothing to hand back and nothing to
   * take. What comes *after* it is the one panel in this pair that is one, since
   * the name card wants a keyboard and a cursor.
   */
  readonly origins: OriginsPanel;

  /**
   * The card that asks the player their name, once (Phase 5 decision 1).
   *
   * A reading panel, unlike the book, and for the one reason that list exists:
   * it wants a keyboard and a cursor, so the pointer lock has to come back for
   * it. Since the owner's ruling of 2026-08-24 it is only ever up before the
   * pointer lock exists at all — both of its askings are in the start menus
   * (`hud/origins.ts`), and the ruling's sharpening took the in-world card away
   * for good — so that reason is now inherited rather than exercised, and it
   * stays in the list because what a panel *wants* is a property of the panel and
   * not of the moment somebody shows it.
   */
  readonly name: NamePrompt;

  /** The four keys, shown once and dismissed on the first input. `null` without a session. */
  readonly keys: KeyCard | null;

  /** The replay export, as a thing a player can click. `null` without a session. */
  readonly save: SaveButton | null;

  private readonly readout: HTMLElement;
  private readonly overlay: HTMLElement;
  private readonly notice: HTMLElement;

  /**
   * The HUD's own root, kept so one class can be written on it.
   *
   * That class is the yield rule (chunk U-1): while a subtitle holds the centre
   * of the frame the aiming prompt and the refusal line dim, and the two of them
   * are siblings of the speech column rather than children, so the fact has to
   * be stated somewhere both can see. The root is that somewhere, and
   * `.hud--touch` is the precedent — a fact about the whole layer, written where
   * the whole layer can read it.
   */
  private readonly root: HTMLElement;

  /**
   * The bottom-anchored column everything anybody says lives in (chunk U-1).
   *
   * **One column, and the band is always above the answer** — the owner's
   * stacking ruling of 2026-08-31, and it is a flex box rather than two `bottom`
   * values because an answer is one line about a boulder or six about a war and
   * a band pinned at a fixed offset would sit on top of the tall one.
   *
   * The two children are appended answer-first and drawn bottom-up
   * (`ui/hud.css` uses `column-reverse`), which is the one arrangement that
   * satisfies both readers of this DOM: the screen wants the band on top, and
   * five checks and smoke scripts ask `querySelector(".hud__dialogue-text")`
   * without qualifying it and have meant *the answer* since Phase 3.
   */
  private readonly speech: HTMLElement;

  /**
   * The blackout a night is slept through behind (`main.ts`, Phase 5 decision 8).
   *
   * Last in document order and therefore over everything, including the
   * readout: a night that passes with a frame rate counter legible through it
   * is a night that reads as a loading screen. It is *not* a loading screen —
   * every tick under it genuinely runs — but it is the one moment in a session
   * where the world moves faster than a player can follow, and covering the
   * whole frame is the honest way to say so.
   *
   * It inherits `.hud`'s `pointer-events: none`, so it never takes the click
   * that would put the pointer lock back.
   */
  private readonly fade: HTMLElement;

  /**
   * What the dark says while the world is crossing a night (`main.ts`'s
   * `runSleep`, chunk Fx-2).
   *
   * **Inside the fade rather than beside it**, and that is the whole reason it
   * exists as a third element: the fade is last in document order and covers
   * everything, including the notice — so the wave-2 playtest's *"pressed X to
   * wait and I'm stuck on a black screen"* was a minute of black with the one
   * line that would have explained it drawn underneath.
   *
   * It carries the date and the world's own hour, and it breathes
   * (`styles.css`), because what the finding actually reported was the absence
   * of a heartbeat: a screen that never changes is a screen that has hung,
   * whatever is written on it.
   */
  private readonly crossing: HTMLElement;

  /**
   * The second line under it: what the one key that still works would do
   * (`main.ts`'s rest crossing, chunk Fx-5).
   *
   * Its own element rather than a second line of text in {@link Hud.crossing},
   * for the reason every prompt in this HUD has its own: the two are different
   * kinds of sentence and only one of them is always there. What is happening is
   * true of every crossing; an offer is true of the two the *body* is in, and a
   * night being slept through has nothing to offer anybody.
   *
   * **It stopped being a footnote at chunk U-1.** The finding of 2026-09-01 was
   * *"I got knocked out, and pressing B doesn't appear to do much?"* — which was
   * this line answering, correctly, in the quietest type on an otherwise empty
   * black screen. It is now a row the size of the beat above it with the key in
   * a gold chip, which is the same shape every other prompt in this HUD took at
   * the same time.
   */
  private readonly option: HTMLElement;

  /** The chip in front of it: which key, or which button under a thumb. */
  private readonly optionKey: HTMLElement;

  /** What the chip is in front of, held as the text node it is so the chip survives a rewrite. */
  private readonly optionLine: Text;

  /**
   * The two of them as one stack (chunk U-1).
   *
   * A wrapper rather than two absolutely-positioned lines, and it buys three
   * things: the option sits under the beat whatever the beat's length, the
   * breathing is **one** animation on one element instead of two that have to be
   * kept in step by hand, and the whole stack can be moved off the touch pad in
   * one rule (`ui/hud.css`'s narrow-frame section) rather than two.
   */
  private readonly crossingStack: HTMLElement;

  /** How dark it is now, so an unchanged frame writes nothing. */
  private faded = 0;

  /** What the crossing line says now, on the same terms. */
  private crossed: string | null = null;

  /** And what the line under it says, on the same terms again. */
  private offered: string | null = null;

  /** Whether a subtitle is holding the centre, so an unchanged frame writes nothing. */
  private yielding = false;

  private sinceRefresh = REFRESH_SECONDS;

  /**
   * `touch` is whether this session is played with fingers (`input/touch.ts`).
   *
   * It reaches exactly one thing: the line at the bottom of each reading panel
   * that says which key shuts it. On a phone there is no key, so the hint names
   * the button instead — and the six panels that carry one are told rather than
   * asking, on the rule the rest of the boot sequence follows (`main.ts` reads
   * the mode once and passes the answer). Defaulted, so every construction with
   * no session behind it — the self-checks, chiefly — is unchanged.
   */
  constructor(root: HTMLElement, session?: SessionChrome, touch = false) {
    this.root = root;

    this.readout = document.createElement("div");
    this.readout.className = "hud__readout";

    this.notice = document.createElement("div");
    this.notice.className = "hud__notice";
    this.notice.hidden = true;

    this.overlay = document.createElement("div");
    // The kit's shadow for type over the world, composed alongside (plan D2).
    this.overlay.className = "hud__overlay hud-text";
    this.overlay.hidden = true;

    this.fade = document.createElement("div");
    this.fade.className = "hud__fade";
    this.fade.hidden = true;

    this.crossing = document.createElement("div");
    this.crossing.className = "hud__crossing";
    this.crossing.hidden = true;

    this.optionKey = document.createElement("kbd");
    this.optionKey.className = "hud__crossing-key";
    // **Which key the dark's one offer names, written once** (chunk U-1): it is
    // the same answer all session — `B` on a keyboard, and the pad's own *rest*
    // button under a thumb, which is what `hud/crossing.ts` has spelled since
    // Fx-5. Only the half of the sentence that says what the press *does*
    // arrives per frame, through {@link Hud.setCrossing}.
    this.optionKey.textContent = crossingKey(touch);

    this.optionLine = document.createTextNode("");

    this.option = document.createElement("div");
    this.option.className = "hud__crossing-option";
    this.option.hidden = true;
    this.option.append(this.optionKey, this.optionLine);

    this.crossingStack = document.createElement("div");
    this.crossingStack.className = "hud__crossing-stack";
    this.crossingStack.hidden = true;
    this.crossingStack.append(this.crossing, this.option);
    // The stack inside the fade, for the crossing line's own reason: the fade
    // covers everything else in this HUD, so a line drawn beside it is a line
    // drawn underneath it.
    this.fade.append(this.crossingStack);

    // The panels attach themselves, and they attach first: everything after
    // them in document order is drawn over them, which is what puts the
    // click-to-play overlay on top of a panel somebody left open rather than
    // under it.
    this.prompt = new InteractPrompt(root);
    this.camp = new InteractPrompt(root, "camp");
    this.word = new InteractPrompt(root, "word");
    // Before the aiming prompt in colour and above it on screen, and after it
    // here only because the constructor order is the document order and the
    // dialogue below has to cover the condition line rather than the other way
    // round (see the two members' notes).
    this.swing = new InteractPrompt(root, "swing");
    this.loose = new InteractPrompt(root, "loose");
    this.condition = new InteractPrompt(root, "condition");
    this.feedback = new FeedbackLine(root);
    // **After the prompts and before everything that covers them**, which is
    // the whole of what "where a thing is built" means in this HUD — the
    // constructor order is the document order and there is no `z-index`
    // anywhere (invariant 1). So the strip sits over the world and under the
    // speech column, the menu, the readout and the night's blackout, all of
    // which are appended after it. It takes no pointer events: the corner strip
    // is a readout of the keys, and the slots a finger presses are the ones on
    // the inventory page (`hud/panels/shared.ts`'s `Hotbar`).
    this.hotbar = new Hotbar();
    root.append(this.hotbar.strip("hud"));
    // **The speech column takes the place in document order the two lines used
    // to hold**, which is what keeps everything after them drawn over them and
    // the condition line under them (chunk U-1). The answer is appended first
    // and drawn last, and the note on {@link Hud.speech} says why it has to be
    // that way round.
    this.speech = document.createElement("div");
    this.speech.className = "hud__speech";
    root.append(this.speech);
    this.dialogue = new DialogueLine(this.speech);
    this.overheard = new DialogueLine(this.speech, "overheard");
    this.market = new MarketPanel(root, touch);
    this.book = new BookPanel(root, touch);
    // **The menu takes the journal's place in the stack** (chunk U-3), which is
    // the whole of what "where a panel is built" means here: it absorbed the
    // journal, the map and the hands, so it stands where the first of them
    // stood and everything appended after it still covers it.
    this.menu = new MenuScreen(root, touch);
    // And the four pages fill themselves. Each surface is handed one page and
    // never the screen ({@link MenuTab}), which is what keeps `map/panel.ts` —
    // outside `hud/` — from having to import a menu to be a page of one.
    this.journal = new JournalOverlay(this.menu.tab("journal"), this.menu.tab("you"));
    this.trace = new TracePanel(root, touch);
    this.inventory = new InventoryPanel(this.menu.tab("inventory"), touch, this.hotbar);
    this.map = new MapPanel(this.menu.tab("map"), touch);
    this.keys = session === undefined ? null : new KeyCard(root, session.keys);
    this.save =
      session === undefined
        ? null
        : new SaveButton(root, session.saveLabel, session.saveHint, session.onSave);

    // Last of the panels, and the document order is the whole reason: the Book
    // of Eras is read against a darkened screen when it opens a session, and the
    // two pieces of session chrome above — the key card and the save button —
    // are exactly what should be under that rather than floating over the page.
    // They belong to the session, and the session has not started yet.
    this.eras = new ErasPanel(root);
    // And after the book, because it is what the book closes into: the birth
    // screen is read against the same darkened frame and is the last thing on
    // the page before the session starts. Its own backdrop rather than a shared
    // one, so the two are independent — the book is shut before this opens, and
    // a shared element would make that ordering load-bearing.
    this.origins = new OriginsPanel(root);
    // **Last of all, and the birth screen is why.** The name card is asked
    // *over* the lives — the screen stays up behind it, so the start flow is one
    // darkened frame from the first page of the book to the last keystroke
    // (`hud/origins.ts`) — and this HUD orders by document position alone, with
    // no `z-index` anywhere in the stylesheet. Built with the other reading
    // panels until wave 3C, where a screenshot showed the card drawn *behind*
    // the lives it was asking about. Nothing else moves: the readout, the
    // notice, the click-to-play overlay and the night's blackout are appended
    // after every panel and still cover this one.
    this.name = new NamePrompt(root);

    root.append(this.readout, this.notice, this.overlay, this.fade);
  }

  /**
   * Whether a panel the player is *reading* is open.
   *
   * **The one place the client asks that question** (Phase 5 decision 10). It
   * used to be spelled `hud.journal.open || hud.trace.open` at three separate
   * sites in `main.ts` — the overlay's answer to "why is the mouse free", the
   * before-and-after of the toggle handling — and a fourth panel joining them
   * meant finding all three and hoping. Now a reading panel says so here and
   * the pointer-lock handshake is written once.
   *
   * Reading panels only: the market board is not one, and since Phase 5 neither
   * is the book. Both are read standing in front of them with the pointer still
   * locked, because closing one is the same F that opened it and a player who
   * walked up to a thing did not ask for their mouse back
   * (`witness/witness.ts`). What is in this list is what the player **sat down
   * with** — the menu, the trace view — and the name card, which is in it
   * because it wants a keyboard and a cursor and there is nothing in the world
   * it is standing in front of. Since the ruling of 2026-08-24 nothing on this
   * list can open in a *playing* session at all: the name is asked in the start
   * menus and the claim that follows it is silent, so every panel here is one
   * the player opened themselves.
   *
   * **Three entries became one at chunk U-3.** The journal, the map and the
   * inventory were on this list separately and are pages of {@link Hud.menu}
   * now; each of their `open` getters is a question about the screen, so
   * keeping them here would have asked it four times. What the list is for is
   * unchanged and so is every answer it gives.
   */
  get reading(): boolean {
    return this.menu.open || this.trace.open || this.name.open;
  }

  /**
   * Rewrites the readout, at most ten times a second.
   *
   * Throttled because the HUD is the only thing in the frame that touches the
   * DOM, and a text node rewritten at 120 Hz costs more than everything else the
   * renderer does with the numbers it displays.
   */
  update(state: HudState, frameSeconds: number): void {
    // Before the throttle, not after: a spoken line's fade is an animation, and
    // one advanced ten times a second steps instead of fading. The refusal line
    // is on the same clock and for the same reason, and it fades faster.
    this.feedback.update(frameSeconds);
    this.dialogue.update(frameSeconds);
    this.overheard.update(frameSeconds);

    // **The yield rule** (chunk U-1, proposed at `ui-lab/surfaces/hud.html:57`,
    // and the owner's call at the playtest): while a subtitle holds the centre
    // of the frame, the aiming prompt and the refusal line under the crosshair
    // drop to a quarter. Asked here rather than in `witness/` because it is a
    // fact about two lines being on screen at once and nothing else.
    //
    // Before the throttle, with the fades: the band is *going* for 1.6 of its
    // 8.6 seconds and a rule that only looked ten times a second would let the
    // prompt come back a fifth of a second late, on the frame the eye is already
    // back on it. Written only when it moved, which is the same latch the fade
    // and the crossing lines keep.
    const yielding = this.overheard.open;

    if (yielding !== this.yielding) {
      this.yielding = yielding;
      this.root.classList.toggle("hud--subtitle", yielding);
    }

    this.sinceRefresh += frameSeconds;

    if (this.sinceRefresh < REFRESH_SECONDS) {
      return;
    }

    this.sinceRefresh = 0;

    const { x, y, z } = state.position;
    const airborne = state.standing ? "" : "  (airborne)";
    const queued = state.pendingUploads > 0 ? ` (+${state.pendingUploads} queued)` : "";
    const columns = `${state.columnCount} column${state.columnCount === 1 ? "" : "s"}`;
    const lines = [
      `${state.fps.toFixed(0)} fps  ·  ${state.drawCalls} draws`,
      `xyz  ${x.toFixed(2)}  ${y.toFixed(2)}  ${z.toFixed(2)}${airborne}`,
      `chunks  ${state.chunkCount} in ${columns}${queued}  ·  ${state.generatedChunks} generated`,
      `tick  ${state.tick}  ·  dug  ${state.dugTiles}`,
      `hash  ${state.worldHash}`,
    ];

    if (state.date !== null) {
      // Under the hash rather than above the frame rate: it is a fact about the
      // world and the two lines above it are the world's other two.
      lines.push(`date  ${formatWorldDate(state.date)}`);
    }

    if (state.droppedSteps > 0) {
      // Only shown once it has happened: a permanent zero teaches the reader to
      // ignore the line, which is the opposite of what it is for.
      const dropped = `${state.droppedSteps} tick${state.droppedSteps === 1 ? "" : "s"}`;
      const frames = `${state.clampedFrames} frame${state.clampedFrames === 1 ? "" : "s"}`;

      lines.push(`behind  ${dropped} dropped in ${frames}`);
    }

    this.readout.textContent = lines.join("\n");
  }

  /** Shows or hides the click-to-play overlay. */
  setOverlay(message: string | null): void {
    this.overlay.hidden = message === null;
    this.overlay.textContent = message ?? "";
  }

  /** Shows or hides a one-line warning. Used for self-check failures in development. */
  setNotice(message: string | null): void {
    this.notice.hidden = message === null;
    this.notice.textContent = message ?? "";
  }

  /**
   * Darkens the whole frame, from 0 (nothing) to 1 (black).
   *
   * Driven off *frame* time rather than stepped time, unlike everything in the
   * witnessing layer, and that is the right clock for it: the world is being
   * fast-forwarded underneath, so stepped seconds are exactly the thing this
   * animation must not be measured in — a fade that ran on the sim's clock
   * would be over in the first frame of a skipped night.
   */
  setFade(level: number): void {
    const clamped = Math.min(Math.max(level, 0), 1);

    // Written only when it moved, like the interact prompt's text
    // (`hud/panels.ts`): this is called every frame of every session and it is
    // zero on almost all of them, and a style write at frame rate costs more
    // than the fade it is animating.
    if (clamped === this.faded) {
      return;
    }

    this.faded = clamped;
    this.fade.hidden = clamped <= 0;
    this.fade.style.opacity = clamped.toFixed(3);
  }

  /**
   * Says what is happening in the dark, and what the player may still do about
   * it — or takes both lines away with `null`.
   *
   * **The wait crossing's own voice** (chunk Fx-2, off the wave-2 playtest:
   * *"pressed X to wait and I'm stuck on a black screen … took like a minute"*).
   * It is drawn *inside* {@link Hud.setFade}'s blackout, which is the only place
   * a line survives it, and it is deliberately not a bar and not a percentage:
   * what a crossing owes the player is the world's own clock moving, and the
   * words for it are `main.ts`'s.
   *
   * **The second line is the offer** (chunk Fx-5): a body that has gone under
   * still has one key that means something, and a body lying up on purpose has
   * one that stops it. Defaulted to `null`, so the two crossings a player
   * pressed a key for pass one argument and say nothing more.
   *
   * Written only when it moves, like every other thing the frame loop touches —
   * each line on its own terms, because the state clause changes on the hour and
   * the offer changes when somebody presses something.
   */
  setCrossing(line: string | null, option: string | null = null): void {
    if (line !== this.crossed) {
      this.crossed = line;
      this.crossing.hidden = line === null;
      this.crossing.textContent = line ?? "";
    }

    if (option !== this.offered) {
      this.offered = option;
      this.option.hidden = option === null;
      // The text node beside the chip rather than `textContent`, which would
      // take the chip out of the document with it — the aiming prompt's own
      // arrangement, for the same reason (`hud/panels/prompt.ts`).
      this.optionLine.textContent = option ?? "";
    }

    // The stack goes with them, so an empty box is not left breathing on an
    // animation nothing can see.
    this.crossingStack.hidden = line === null && option === null;
  }
}
